/**
 * Marketing Materials Panel
 * Displays suggested and attached marketing materials for a document
 * 
 * Features:
 * - Auto-suggests materials based on line item products/categories
 * - Shows attached materials with options
 * - Preview brochures in BookViewer
 * - Preview videos inline
 * 
 * @version 1.0.0
 * @date December 2025
 */

'use client';

import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  FileText, Video, Image, Presentation, Mail, File, Sparkles,
  Plus, Trash2, Eye, ExternalLink, Link2, Mail as MailIcon,
  Download, ChevronDown, ChevronUp, Check, X, Info, Settings,
  Paperclip, BookOpen, Play, Search
} from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';
import { useMarketingMaterials } from '@/hooks/useMarketingMaterials';
import type { MarketingMaterial, AttachedMaterial, AttachMaterialOptions } from '@/hooks/useMarketingMaterials';
import BookViewer from './BookViewer';
import toast from 'react-hot-toast';

// ============================================================================
// TYPES
// ============================================================================

interface LineItem {
  item_id?: number;
  category_id?: number;
  [key: string]: any;
}

interface MarketingMaterialsPanelProps {
  workItemId?: string; // If editing existing document
  lineItems: LineItem[];
  isNewDocument: boolean;
  onMaterialsChange?: (materials: AttachMaterialOptions[]) => void;
  readOnly?: boolean;
}

// ============================================================================
// MATERIAL TYPE ICONS
// ============================================================================

const MaterialTypeIcons: Record<string, React.ElementType> = {
  brochure: BookOpen,
  video: Video,
  pdf: FileText,
  image: Image,
  presentation: Presentation,
  email: Mail,
  html: File
};

const MaterialTypeColors: Record<string, string> = {
  brochure: 'text-blue-500 bg-blue-50',
  video: 'text-purple-500 bg-purple-50',
  pdf: 'text-red-500 bg-red-50',
  image: 'text-green-500 bg-green-50',
  presentation: 'text-orange-500 bg-orange-50',
  email: 'text-teal-500 bg-teal-50',
  html: 'text-gray-500 bg-gray-50'
};

// ============================================================================
// COMPONENT
// ============================================================================

export default function MarketingMaterialsPanel({
  workItemId,
  lineItems,
  isNewDocument,
  onMaterialsChange,
  readOnly = false
}: MarketingMaterialsPanelProps) {
  const { currentTheme } = useTheme();
  const isDark = currentTheme.mode === 'dark';

  const {
    suggestions,
    libraryMaterials,
    attachedMaterials,
    loading,
    error,
    getSuggestions,
    getLibraryMaterials,
    getAttachedMaterials,
    attachMaterialIds,
    detachMaterial,
    updateMaterialSettings
  } = useMarketingMaterials();

  // Local state for new documents (before save)
  const [pendingMaterials, setPendingMaterials] = useState<AttachMaterialOptions[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(true);
  const [showLibrary, setShowLibrary] = useState(true);
  const [showBookViewer, setShowBookViewer] = useState(false);
  const [bookViewerDocId, setBookViewerDocId] = useState<number | null>(null);
  const [expandedSettings, setExpandedSettings] = useState<string | null>(null);
  const [libraryQuery, setLibraryQuery] = useState('');

  // Extract product and category IDs from line items
  const { productIds, categoryIds } = useMemo(() => {
    const products = new Set<number>();
    const categories = new Set<number>();
    
    lineItems.forEach(item => {
      if (item.item_id) products.add(Number(item.item_id));
      if (item.category_id) categories.add(Number(item.category_id));
    });
    
    return {
      productIds: Array.from(products),
      categoryIds: Array.from(categories)
    };
  }, [lineItems]);

  // Load suggestions when line items change
  useEffect(() => {
    void getSuggestions(productIds, categoryIds);
  }, [productIds, categoryIds, getSuggestions]);

  // Load attached materials for existing documents
  useEffect(() => {
    if (workItemId && !isNewDocument) {
      getAttachedMaterials(workItemId);
    }
  }, [workItemId, isNewDocument, getAttachedMaterials]);

  useEffect(() => {
    if (!readOnly) {
      void getLibraryMaterials();
    }
  }, [getLibraryMaterials, readOnly]);

  // Notify parent of pending materials changes
  useEffect(() => {
    if (onMaterialsChange) {
      onMaterialsChange(pendingMaterials);
    }
  }, [pendingMaterials, onMaterialsChange]);

  // Get materials to display (pending for new, attached for existing)
  const displayedMaterials = isNewDocument ? pendingMaterials : attachedMaterials;

  // Filter suggestions to exclude already attached/pending
  const availableSuggestions = useMemo(() => {
    const attachedIds = new Set(
      isNewDocument 
        ? pendingMaterials.map(m => m.material_id)
        : attachedMaterials.map(m => m.material_id)
    );
    return suggestions.filter(s => !attachedIds.has(s.id));
  }, [suggestions, pendingMaterials, attachedMaterials, isNewDocument]);

  const availableLibrary = useMemo(() => {
    const attachedIds = new Set(
      isNewDocument
        ? pendingMaterials.map(m => m.material_id)
        : attachedMaterials.map(m => m.material_id)
    );
    const suggestedIds = new Set(availableSuggestions.map(material => material.id));

    return libraryMaterials.filter((material) => !attachedIds.has(material.id) && !suggestedIds.has(material.id));
  }, [attachedMaterials, availableSuggestions, isNewDocument, libraryMaterials, pendingMaterials]);

  // Handlers
  const handleAttachMaterial = useCallback(async (material: MarketingMaterial) => {
    const linkSource = material.matchReason === 'product'
      ? 'product'
      : material.matchReason === 'category'
        ? 'category'
        : 'manual';

    const options: AttachMaterialOptions = {
      material_id: material.id,
      link_source: linkSource,
      source_product_ids: material.matchedProducts || [],
      source_category_ids: material.matchedCategories || [],
      include_in_email: true,
      include_as_link: true
    };

    if (isNewDocument) {
      setPendingMaterials(prev => [...prev, options]);
      toast.success(`Added "${material.title}" to materials`);
    } else if (workItemId) {
      const result = await attachMaterialIds(workItemId, [material.id], options.link_source);
      if (result.attached > 0) {
        toast.success(`Attached "${material.title}"`);
      }
    }
  }, [isNewDocument, workItemId, attachMaterialIds]);

  const handleDetachMaterial = useCallback(async (materialId: number, title: string) => {
    if (isNewDocument) {
      setPendingMaterials(prev => prev.filter(m => m.material_id !== materialId));
      toast.success(`Removed "${title}"`);
    } else if (workItemId) {
      const success = await detachMaterial(workItemId, materialId);
      if (success) {
        toast.success(`Removed "${title}"`);
      }
    }
  }, [isNewDocument, workItemId, detachMaterial]);

  const handleToggleSetting = useCallback(async (
    attachmentId: string, 
    materialId: number,
    setting: 'include_in_email' | 'include_as_link' | 'include_as_attachment',
    currentValue: boolean
  ) => {
    if (isNewDocument) {
      setPendingMaterials(prev => 
        prev.map(m => m.material_id === materialId ? { ...m, [setting]: !currentValue } : m)
      );
    } else {
      await updateMaterialSettings(attachmentId, { [setting]: !currentValue });
    }
  }, [isNewDocument, updateMaterialSettings]);

  const handlePreview = useCallback((material: MarketingMaterial | AttachedMaterial) => {
    const materialType = material.material_type;
    const materialId = 'material_id' in material ? material.material_id : material.id;

    if (materialType === 'brochure' && material.page_count && material.page_count > 1) {
      setBookViewerDocId(materialId);
      setShowBookViewer(true);
    } else if (materialType === 'video' && material.file_url) {
      window.open(material.file_url, '_blank');
    } else if (material.file_url) {
      window.open(material.file_url, '_blank');
    }
  }, []);

  const formatFileSize = (bytes?: number) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const handleLibrarySearch = useCallback(async () => {
    await getLibraryMaterials(libraryQuery);
  }, [getLibraryMaterials, libraryQuery]);

  // ============================================================================
  // RENDER
  // ============================================================================

  if (loading && !suggestions.length && !displayedMaterials.length) {
    return (
      <div className={`p-4 rounded-lg border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
        <div className="flex items-center gap-2 text-gray-500">
          <div className="animate-spin h-4 w-4 border-2 border-current border-t-transparent rounded-full" />
          <span>Checking for marketing materials...</span>
        </div>
      </div>
    );
  }

  return (
    <div className={`rounded-lg border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
      {/* Header */}
      <div className={`px-4 py-3 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Paperclip className={`h-5 w-5 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
            <h3 className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Marketing Materials
            </h3>
            {displayedMaterials.length > 0 && (
              <span className={`px-2 py-0.5 text-xs rounded-full ${isDark ? 'bg-blue-900 text-blue-300' : 'bg-blue-100 text-blue-700'}`}>
                {displayedMaterials.length} attached
              </span>
            )}
          </div>
          {availableSuggestions.length > 0 && (
            <button
              onClick={() => setShowSuggestions(!showSuggestions)}
              className={`flex items-center gap-1 text-sm ${isDark ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}
            >
              <Sparkles className="h-4 w-4 text-yellow-500" />
              {availableSuggestions.length} suggested
              {showSuggestions ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </button>
          )}
        </div>
      </div>

      {/* Suggestions */}
      {showSuggestions && availableSuggestions.length > 0 && !readOnly && (
        <div className={`px-4 py-3 border-b ${isDark ? 'border-gray-700 bg-gray-800/50' : 'border-gray-100 bg-blue-50/30'}`}>
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="h-4 w-4 text-yellow-500" />
            <span className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
              Suggested for items in this document
            </span>
          </div>
          <div className="space-y-2">
            {availableSuggestions.map(material => {
              const Icon = MaterialTypeIcons[material.material_type] || File;
              const colorClass = MaterialTypeColors[material.material_type] || 'text-gray-500 bg-gray-50';
              
              return (
                <div
                  key={material.id}
                  className={`flex items-center justify-between p-2 rounded-lg ${isDark ? 'bg-gray-700/50 hover:bg-gray-700' : 'bg-white hover:bg-gray-50'} transition-colors`}
                >
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className={`p-2 rounded-lg ${isDark ? 'bg-gray-600' : colorClass}`}>
                      <Icon className={`h-4 w-4 ${isDark ? 'text-gray-300' : ''}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className={`font-medium text-sm truncate ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        {material.title}
                      </div>
                      <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                        {material.material_type} 
                        {material.page_count && material.page_count > 1 && ` • ${material.page_count} pages`}
                        {material.duration && ` • ${Math.floor(material.duration / 60)}:${String(material.duration % 60).padStart(2, '0')}`}
                        {material.file_size && ` • ${formatFileSize(material.file_size)}`}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handlePreview(material)}
                      className={`p-1.5 rounded-lg transition-colors ${isDark ? 'hover:bg-gray-600 text-gray-400' : 'hover:bg-gray-200 text-gray-500'}`}
                      title="Preview"
                    >
                      <Eye className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleAttachMaterial(material)}
                      className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
                    >
                      <Plus className="h-3.5 w-3.5" />
                      Add
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {!readOnly && (
        <div className={`px-4 py-3 border-b ${isDark ? 'border-gray-700 bg-gray-800/40' : 'border-gray-100 bg-gray-50/70'}`}>
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="flex items-center gap-2">
                <Search className="h-4 w-4 text-blue-500" />
                <span className={`text-sm font-medium ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
                  Browse material library
                </span>
              </div>
              <p className={`mt-1 text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                Manually attach brochures, PDFs, videos, and presentations even when this proposal has no auto-suggestion signals.
              </p>
            </div>
            <button
              type="button"
              onClick={() => setShowLibrary((previous) => !previous)}
              className={`flex items-center gap-1 text-sm ${isDark ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}
            >
              {availableLibrary.length > 0 && <span>{availableLibrary.length} available</span>}
              {showLibrary ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </button>
          </div>

          {showLibrary && (
            <div className="mt-3 space-y-3">
              <div className="flex flex-col gap-2 sm:flex-row">
                <label className="sr-only" htmlFor={`marketing-library-search-${workItemId || 'new'}`}>
                  Search marketing materials
                </label>
                <div className="relative flex-1">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                  <input
                    id={`marketing-library-search-${workItemId || 'new'}`}
                    type="text"
                    value={libraryQuery}
                    onChange={(event) => setLibraryQuery(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        event.preventDefault();
                        void handleLibrarySearch();
                      }
                    }}
                    placeholder="Search brochures, PDFs, videos, or presentations"
                    className={`w-full rounded-lg border py-2 pl-9 pr-3 text-sm ${isDark ? 'border-gray-600 bg-gray-900 text-gray-100 placeholder:text-gray-500' : 'border-gray-200 bg-white text-gray-900 placeholder:text-gray-400'}`}
                  />
                </div>
                <button
                  type="button"
                  onClick={() => void handleLibrarySearch()}
                  className="rounded-lg bg-blue-600 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700"
                >
                  Search
                </button>
              </div>

              {availableLibrary.length > 0 ? (
                <div className="space-y-2">
                  {availableLibrary.slice(0, 8).map((material) => {
                    const Icon = MaterialTypeIcons[material.material_type] || File;
                    const colorClass = MaterialTypeColors[material.material_type] || 'text-gray-500 bg-gray-50';

                    return (
                      <div
                        key={`library-${material.id}`}
                        className={`flex items-center justify-between rounded-lg p-2 transition-colors ${isDark ? 'bg-gray-700/40 hover:bg-gray-700' : 'bg-white hover:bg-gray-50'}`}
                      >
                        <div className="flex min-w-0 flex-1 items-center gap-3">
                          <div className={`rounded-lg p-2 ${isDark ? 'bg-gray-600' : colorClass}`}>
                            <Icon className={`h-4 w-4 ${isDark ? 'text-gray-300' : ''}`} />
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className={`truncate text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                              {material.title}
                            </div>
                            <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                              {material.material_type}
                              {material.file_size && ` • ${formatFileSize(material.file_size)}`}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => handlePreview(material)}
                            className={`rounded-lg p-1.5 transition-colors ${isDark ? 'text-gray-400 hover:bg-gray-600' : 'text-gray-500 hover:bg-gray-200'}`}
                            title="Preview"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleAttachMaterial(material)}
                            className="flex items-center gap-1 rounded-lg bg-blue-600 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-blue-700"
                          >
                            <Plus className="h-3.5 w-3.5" />
                            Add
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className={`rounded-lg border border-dashed px-3 py-3 text-sm ${isDark ? 'border-gray-700 text-gray-400' : 'border-gray-200 text-gray-500'}`}>
                  {libraryQuery.trim()
                    ? 'No marketing materials matched that search yet. Try a broader keyword or clear the filter.'
                    : 'No unlinked marketing materials are currently available in the library for this client.'}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Attached Materials */}
      {displayedMaterials.length > 0 && (
        <div className="divide-y divide-gray-200 dark:divide-gray-700">
          {displayedMaterials.map((item, index) => {
            const material = isNewDocument
              ? suggestions.find(s => s.id === (item as AttachMaterialOptions).material_id)
              : (item as AttachedMaterial);
            
            if (!material) return null;
            
            const materialId = isNewDocument 
              ? (item as AttachMaterialOptions).material_id 
              : (item as AttachedMaterial).material_id;
            const attachmentId = isNewDocument ? '' : (item as AttachedMaterial).attachment_id;
            const isExpanded = expandedSettings === `${materialId}`;
            
            const Icon = MaterialTypeIcons[material.material_type] || File;
            const colorClass = MaterialTypeColors[material.material_type] || 'text-gray-500 bg-gray-50';
            
            // Get settings from item
            const includeInEmail = isNewDocument 
              ? (item as AttachMaterialOptions).include_in_email ?? true
              : (item as AttachedMaterial).include_in_email;
            const includeAsLink = isNewDocument
              ? (item as AttachMaterialOptions).include_as_link ?? true
              : (item as AttachedMaterial).include_as_link;
            const includeAsAttachment = isNewDocument
              ? (item as AttachMaterialOptions).include_as_attachment ?? false
              : (item as AttachedMaterial).include_as_attachment;

            return (
              <div key={materialId} className={`p-3 ${isDark ? 'hover:bg-gray-700/30' : 'hover:bg-gray-50'}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className={`p-2 rounded-lg ${isDark ? 'bg-gray-700' : colorClass}`}>
                      <Icon className={`h-4 w-4 ${isDark ? 'text-gray-300' : ''}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className={`font-medium text-sm truncate ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        {material.title}
                      </div>
                      <div className="flex items-center gap-2 mt-0.5">
                        {includeInEmail && (
                          <span className={`inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded ${isDark ? 'bg-green-900/50 text-green-400' : 'bg-green-100 text-green-700'}`}>
                            <MailIcon className="h-3 w-3" /> Email
                          </span>
                        )}
                        {includeAsLink && (
                          <span className={`inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded ${isDark ? 'bg-blue-900/50 text-blue-400' : 'bg-blue-100 text-blue-700'}`}>
                            <Link2 className="h-3 w-3" /> Link
                          </span>
                        )}
                        {includeAsAttachment && (
                          <span className={`inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded ${isDark ? 'bg-purple-900/50 text-purple-400' : 'bg-purple-100 text-purple-700'}`}>
                            <Download className="h-3 w-3" /> Download
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => handlePreview(material)}
                      className={`p-1.5 rounded-lg transition-colors ${isDark ? 'hover:bg-gray-600 text-gray-400' : 'hover:bg-gray-200 text-gray-500'}`}
                      title="Preview"
                    >
                      <Eye className="h-4 w-4" />
                    </button>
                    {!readOnly && (
                      <>
                        <button
                          onClick={() => setExpandedSettings(isExpanded ? null : `${materialId}`)}
                          className={`p-1.5 rounded-lg transition-colors ${isExpanded ? (isDark ? 'bg-gray-600' : 'bg-gray-200') : ''} ${isDark ? 'hover:bg-gray-600 text-gray-400' : 'hover:bg-gray-200 text-gray-500'}`}
                          title="Settings"
                        >
                          <Settings className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDetachMaterial(materialId, material.title)}
                          className={`p-1.5 rounded-lg transition-colors ${isDark ? 'hover:bg-red-900/50 text-gray-400 hover:text-red-400' : 'hover:bg-red-50 text-gray-500 hover:text-red-600'}`}
                          title="Remove"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </>
                    )}
                  </div>
                </div>

                {/* Expanded Settings */}
                {isExpanded && !readOnly && (
                  <div className={`mt-3 pt-3 border-t ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                    <div className="grid grid-cols-3 gap-2">
                      <button
                        onClick={() => handleToggleSetting(attachmentId, materialId, 'include_in_email', includeInEmail)}
                        className={`flex items-center justify-center gap-2 p-2 rounded-lg text-sm transition-colors ${
                          includeInEmail
                            ? isDark ? 'bg-green-900/50 text-green-400 border border-green-700' : 'bg-green-100 text-green-700 border border-green-300'
                            : isDark ? 'bg-gray-700 text-gray-400 border border-gray-600' : 'bg-gray-100 text-gray-500 border border-gray-200'
                        }`}
                      >
                        <MailIcon className="h-4 w-4" />
                        In Email
                        {includeInEmail && <Check className="h-3 w-3" />}
                      </button>
                      <button
                        onClick={() => handleToggleSetting(attachmentId, materialId, 'include_as_link', includeAsLink)}
                        className={`flex items-center justify-center gap-2 p-2 rounded-lg text-sm transition-colors ${
                          includeAsLink
                            ? isDark ? 'bg-blue-900/50 text-blue-400 border border-blue-700' : 'bg-blue-100 text-blue-700 border border-blue-300'
                            : isDark ? 'bg-gray-700 text-gray-400 border border-gray-600' : 'bg-gray-100 text-gray-500 border border-gray-200'
                        }`}
                      >
                        <Link2 className="h-4 w-4" />
                        As Link
                        {includeAsLink && <Check className="h-3 w-3" />}
                      </button>
                      <button
                        onClick={() => handleToggleSetting(attachmentId, materialId, 'include_as_attachment', includeAsAttachment)}
                        className={`flex items-center justify-center gap-2 p-2 rounded-lg text-sm transition-colors ${
                          includeAsAttachment
                            ? isDark ? 'bg-purple-900/50 text-purple-400 border border-purple-700' : 'bg-purple-100 text-purple-700 border border-purple-300'
                            : isDark ? 'bg-gray-700 text-gray-400 border border-gray-600' : 'bg-gray-100 text-gray-500 border border-gray-200'
                        }`}
                      >
                        <Download className="h-4 w-4" />
                        Download
                        {includeAsAttachment && <Check className="h-3 w-3" />}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Empty State */}
      {displayedMaterials.length === 0 && availableSuggestions.length === 0 && availableLibrary.length === 0 && !loading && (
        <div className="px-4 py-6 text-center">
          <Paperclip className={`h-8 w-8 mx-auto mb-2 ${isDark ? 'text-gray-600' : 'text-gray-300'}`} />
          <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            {readOnly
              ? 'No marketing materials are attached to this record yet.'
              : 'No marketing materials are attached yet. Browse the library above to add collateral manually.'}
          </p>
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div className="px-4 py-3 bg-red-50 dark:bg-red-900/20 border-t border-red-200 dark:border-red-800">
          <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
        </div>
      )}

      {/* BookViewer Modal */}
      {showBookViewer && bookViewerDocId && (
        <BookViewer
          documentId={bookViewerDocId}
          isOpen={showBookViewer}
          onClose={() => {
            setShowBookViewer(false);
            setBookViewerDocId(null);
          }}
        />
      )}
    </div>
  );
}
